import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as myStatisticsActions } from 'features/myStatistics';
import { formatNumber } from 'shared/utils/formatNumber';

import { convertFiltersBalanceStatistic, convertFiltersStatistic } from './convertFilters';
import { addNotify } from 'features/notify';

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000; // Количество миллисекунд в сутках

export const EStatisticsTab = {
  agentsStatistics: 'agentsStatistics',
  agentsBalance: 'agentsBalance',
  playersBalance: 'playersBalance',
};

export const ETableType = {
  Statistics: 'Statistics',
  Balance: 'Balance',
};

const comparisonNamingList = {
  default: '',
  totalNetwin: 'totalNetwinByDates',
};

const useModel = () => {
  const dispatch = useDispatch();
  const { actionProcessing, statistic } = useSelector(state => state.myStatistics, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);

  const authId = useSelector(state => state.auth.id, shallowEqual);
  const role = useSelector(state => state.auth.role, shallowEqual);

  const [activeTab, setActiveTab] = useState(EStatisticsTab.agentsStatistics);

  const tabs = useMemo(
    () =>
      [
        {
          value: EStatisticsTab.agentsStatistics,
          title: locale.statisticTabs?.[role - 1],
          active: true,
        },
        {
          value: EStatisticsTab.playersBalance,
          title: locale.balanceOfPlayers,
          amount: statistic.totalUserBalance,
          active: false,
        },
        {
          value: EStatisticsTab.agentsBalance,
          title: locale.balanceOf?.[role - 1],
          amount: statistic.totalAgentBalance,
          active: false,
        },
      ].map(tab => ({
        ...tab,
        active: activeTab === tab.value,
      })),
    [activeTab, statistic, locale, role],
  );

  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [comparisonType, setComparisonType] = useState(null);
  const [activeDaySelect, setActiveDaySelect] = useState({
    text: '',
    value: '',
    value2: '',
  });

  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setHours(0, 0, 0, 0) - MILLISECONDS_IN_DAY), // Вчера в 00:00
    to: new Date(new Date().setHours(0, 0, 0, 0)), // Сегодня в 00:00
  });

  const resetActiveDay = useCallback(() => {
    setActiveDaySelect({ text: '', value: '', value2: '' });
  }, []);
  const [isAsc, setIsAsc] = useState(false);

  const setActiveDay = useCallback(day => {
    setActiveDaySelect(day);
    setDateRange(prev => ({
      ...prev,
      from: day.value,
      to: day.value2,
    }));
  }, []);

  const onChangeFrom = useCallback(date => {
    setDateRange(prev => ({ ...prev, from: date }));
    resetActiveDay();
  }, [resetActiveDay]);

  const onChangeTo = useCallback(date => {
    setDateRange(prev => ({ ...prev, to: date }));
    resetActiveDay();
  }, [resetActiveDay]);

  const onChangeComparisonType = useCallback(
    type => {
      if (comparisonType !== type) {
        setComparisonType(type);
        setIsComparisonMode(true);
      } else {
        setIsComparisonMode(false);
        setComparisonType(comparisonNamingList.default);
      }
    },
    [comparisonType],
  );

  const fetchStatistics = useCallback(() => {
    if (dayjs(dateRange.from).isAfter(dayjs(dateRange.to))) {
      dispatch(addNotify(locale.dateRangeError, 'error'));
      return;
    }

    if (activeTab.indexOf(ETableType.Statistics) >= 0) {
      dispatch(
        myStatisticsActions.getStatistic({
          filters: {
            to: dateRange.to,
            from: dateRange.from,
          },
          id: authId,
          isComparisonMode,
          comparisonType,
        }),
      );
    }

    if (activeTab.indexOf(ETableType.Balance) >= 0) {
      const filters = convertFiltersBalanceStatistic({
        role: activeTab === EStatisticsTab.playersBalance ? 0 : role - 1,
        to: dateRange.to,
        from: dateRange.from,
        isAsc: isAsc,
      });

      dispatch(
        myStatisticsActions.getBalanceStatistic({
          filters,
        }),
      );
    }
  }, [dateRange.from, dateRange.to, activeTab, authId, role, dispatch, isAsc, isComparisonMode, comparisonType, locale]);

  const data = useMemo(() => {
    const arr = statistic.totalMetricStatistic.currentPeriod.totalByDate || [];
    return {
      type: 'totalSum',
      data: arr?.map(t => t.count),
      time: arr?.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      originalTime: arr.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      color: 'rgb(233, 167, 34)',
      colorFill: 'rgba(233, 167, 34, 0.3)',
      title: 'Total',
    };
  }, [statistic]);

  const data2 = useMemo(() => {
    if (!isComparisonMode) return [];
    const arr = statistic.totalMetricStatistic.previousPeriod.totalByDate ?? [];
    return {
      type: 'totalSumDiff',
      data: arr.map(t => t.count),
      time: arr.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      originalTime: arr.map(t => dayjs(t.originalDate).subtract(3, 'hour').format('DD.MM')),
      color: '#019CF1',
      colorFill: 'rgba(1, 156, 241, 0.3)',
      title: 'Total',
    };
  }, [statistic, isComparisonMode]);

  const childrenBalance = useMemo(() => {
    if (role === 1) return statistic.balanceStatistic.balanceOfUser;
    if (role === 2) return statistic.balanceStatistic.balanceOfCashier;
    if (role === 3) return statistic.balanceStatistic.balanceOfAdmin;
    return null;
  }, [role, statistic]);

  const buildTopItems = useCallback(
    locale => {
      const s = statistic || {};
      const cp = comparisonType;
      const ap = actionProcessing;

      return [
        {
          key: 'usersCount',
          icon: 'users',
          left: { value: formatNumber(s.totalStatistic?.usersCount, 0), label: locale.users },
          compareKey: 'usersCount',
          diffPercent: s.totalMetricStatistic?.diffPercent,
          diff: s.totalMetricStatistic?.totalDiff,
          showRight: !ap && cp === 'usersCount',
          isComparative: false,
        },
        {
          key: 'activeUsers',
          icon: 'activeUsers',
          left: { value: formatNumber(s.totalStatistic?.activeUsers, 0), label: locale.activeUser },
          compareKey: 'activeUsers',
          diffPercent: s.totalMetricStatistic?.diffPercent.toFixed(2),
          diff: s.totalMetricStatistic?.totalDiff,
          showRight: !ap && cp === 'activeUsers',
          isComparative: true,
        },
        {
          key: 'totalBets',
          icon: 'totalBets',
          left: { value: formatNumber(s.totalStatistic?.totalBets), label: locale.totalBets },
          compareKey: 'totalBets',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalBets',
          isComparative: true,
        },
        {
          key: 'totalWins',
          icon: 'totalWins',
          left: { value: formatNumber(s.totalStatistic?.totalWins), label: locale.totalWin },
          compareKey: 'totalWins',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalWins',
          isComparative: true,
        },
        {
          key: 'totalGGR',
          icon: 'totalGGR',
          left: { value: formatNumber(s.totalStatistic?.totalGGR), label: locale.GGR },
          compareKey: 'totalGGR',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalGGR',
          isComparative: true,
        },
        {
          key: 'margin',
          icon: 'margin',
          left: { value: (s?.totalStatistic?.totalMargin ?? 0) + '%', label: locale.margin },
          compareKey: 'margin',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'margin',
          isComparative: true,
          info: {
            text: locale.margin,
            description: `${locale.margin} = ${locale.GGR} / ${locale.totalBets} * 100%`,
          },
        },
      ];
    },
    [statistic, comparisonType, actionProcessing],
  );

  useEffect(() => {
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsc, activeTab, comparisonType]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      fetchStatistics();
    },
    [fetchStatistics],
  );

  return {
    inputState: {
      values: dateRange,
      activeTab,
      setActiveTab,
      setActiveDay,
      setIsAsc,
      isAsc,
      tabs,
      onChangeFrom,
      onChangeTo,
      onChangeComparisonType,
      buildTopItems,
    },
    outputState: {
      onSubmit,
      childrenBalance,
      data,
      data2,
      statistic,
      isComparisonMode,
      comparisonType,
      activeDaySelect,
      actionProcessing,
    },
  };
};

export default useModel;
